'use strict';

const getVehiclePicker = () =>
	import(/* webpackChunkName: "get-vehicle-picker" */ 'public/js/components/vehicle-picker.js');

const getCart = () =>
	import(/* webpackChunkName: "get-cart" */ 'public/js/components/cart');

const {
	STEER_GA4_EVENT,
	buildSteerAddToCartPayload,
	buildSteerViewItemPayload,
	persistSteerExperienceSession,
	readOptionalPrice,
	readTrimmedString,
	rememberViewedItem,
	resolveSteerVisitorId,
	trackSteerGa4Event,
} = require('public/js/lib/steer-ga4');

const ADD_TO_CART_FAILURE_REASON = {
	CART_MODULE_LOAD_FAILED: 'cart_module_load_failed',
	CART_UNAVAILABLE: 'cart_unavailable',
	MISSING_BRAND: 'missing_brand',
	MISSING_DISPLAYED_PRICING: 'missing_displayed_pricing',
	MISSING_MPN: 'missing_mpn',
	MISSHAPEN_PAYLOAD: 'misshapen_payload',
};

const STEER_SOURCE = 'steer';

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
	return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @param {unknown} detail
 * @returns {Record<string, unknown>}
 */
function normalizeSteerDetail(detail) {
	if (!isPlainObject(detail)) {
		return {};
	}

	return detail;
}

function openVehiclePickerFresh() {
	getVehiclePicker().then((module) => {
		module.getDataAndOpenVehiclePicker({ navigateAfterSelect: false });
	});
}

const STEER_VEHICLE_SELECTED_DOMAIN_KEY = Object.freeze({
	make: 'make',
	model: 'model',
	rearTireSize: 'rear_tire_size',
	tireSize: 'tire_size',
	trim: 'trim',
	year: 'year',
});

/**
 * STEER vehicleSelected → vendor_tire_capture.vehicle_selected (snake_case).
 *
 * @param {unknown} vehicleSelected
 * @returns {Record<string, string|number>}
 */
function parseVehicleSelected(vehicleSelected) {
	if (!isPlainObject(vehicleSelected)) {
		return {};
	}

	/** @type {Record<string, string|number>} */
	const parsed = {};

	Object.entries(STEER_VEHICLE_SELECTED_DOMAIN_KEY).forEach(([steerKey, domainKey]) => {
		if (!Object.hasOwn(vehicleSelected, steerKey)) {
			return;
		}

		const value = vehicleSelected[steerKey];

		if (domainKey === STEER_VEHICLE_SELECTED_DOMAIN_KEY.year) {
			if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
				parsed[domainKey] = value;
			} else if (typeof value === 'string') {
				const trimmed = value.trim();
				if (/^\d+$/.test(trimmed)) {
					const year = Number.parseInt(trimmed, 10);
					if (year > 0) {
						parsed[domainKey] = year;
					}
				}
			}

			return;
		}

		if (typeof value !== 'string') {
			return;
		}

		const trimmed = value.trim();
		if (trimmed !== '') {
			parsed[domainKey] = trimmed;
		}
	});

	return parsed;
}

/**
 * @param {unknown} query
 * @returns {{ tireSize: string }|null}
 */
function parseQuery(query) {
	if (!isPlainObject(query) || typeof query.tireSize !== 'string') {
		return null;
	}

	const tireSize = query.tireSize.trim();

	return tireSize !== '' ? { tireSize } : null;
}

/**
 * @param {unknown} value
 * @returns {boolean|null}
 */
function parseBooleanWireValue(value) {
	if (typeof value === 'boolean') {
		return value;
	}

	if (typeof value === 'number' && Number.isInteger(value)) {
		if (value === 1) {
			return true;
		}

		if (value === 0) {
			return false;
		}
	}

	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		if (normalized === '1' || normalized === 'true') {
			return true;
		}

		if (normalized === '0' || normalized === 'false') {
			return false;
		}
	}

	return null;
}

/**
 * STEER add-to-cart event detail → vendor_tire_capture cart-update shape (snake_case).
 *
 * @param {unknown} detail
 * @returns {Record<string, unknown>|null}
 */
function parseVendorTireCapture(detail) {
	if (!isPlainObject(detail)) {
		return null;
	}

	const parsedFits = parseBooleanWireValue(detail.isTireFitsVehicle);
	const vehicleSelected = parseVehicleSelected(detail.vehicleSelected);
	const query = parseQuery(detail.query);

	const hasCaptureContent = parsedFits !== null
		|| Object.keys(vehicleSelected).length > 0
		|| query !== null;

	if (!hasCaptureContent) {
		return null;
	}

	/** @type {Record<string, unknown>} */
	const vendorTireCapture = {};

	if (parsedFits !== null) {
		vendorTireCapture.fits_selected_vehicle = parsedFits;
	}

	if (Object.keys(vehicleSelected).length > 0) {
		vendorTireCapture.vehicle_selected = vehicleSelected;
	}

	if (query !== null) {
		vendorTireCapture.tire_size_query = query.tireSize;
	}

	return vendorTireCapture;
}

/**
 * @param {Record<string, unknown>} detail
 * @returns {number|null}
 */
function readDisplayedPricingFromDetail(detail) {
	const displayedPricing = readOptionalPrice(detail.displayedPricing);
	if (displayedPricing !== null && displayedPricing > 0) {
		return displayedPricing;
	}

	const displayedPrice = readOptionalPrice(detail.displayedPrice ?? detail.displayed_price);
	if (displayedPrice !== null && displayedPrice > 0) {
		return displayedPrice;
	}

	return null;
}

/**
 * @param {unknown} detail
 * @returns {{ ok: true, payload: { brand: string, mpn: string, quantity: number, displayedPricing: number, catalog?: string, vendorTireCapture: Record<string, unknown>|null } } | { ok: false, reason: string }}
 */
function parseAddToCartPayload(detail) {
	if (!isPlainObject(detail)) {
		return { ok: false, reason: ADD_TO_CART_FAILURE_REASON.MISSHAPEN_PAYLOAD };
	}

	const brand = typeof detail.brand === 'string' ? detail.brand.trim() : '';
	const mpn = typeof detail.mpn === 'string' ? detail.mpn.trim() : '';

	if (brand === '') {
		return { ok: false, reason: ADD_TO_CART_FAILURE_REASON.MISSING_BRAND };
	}

	if (mpn === '') {
		return { ok: false, reason: ADD_TO_CART_FAILURE_REASON.MISSING_MPN };
	}

	const quantityValue = detail.quantity;
	const hasValidQuantity =
		typeof quantityValue === 'number'
		&& Number.isFinite(quantityValue)
		&& Number.isInteger(quantityValue)
		&& quantityValue > 0;
	const quantity = hasValidQuantity ? quantityValue : 1;
	const displayedPricing = readDisplayedPricingFromDetail(detail);
	if (displayedPricing === null) {
		return { ok: false, reason: ADD_TO_CART_FAILURE_REASON.MISSING_DISPLAYED_PRICING };
	}

	const catalog = readTrimmedString(detail.catalog);
	/** @type {{ brand: string, mpn: string, quantity: number, displayedPricing: number, catalog?: string, vendorTireCapture: Record<string, unknown>|null }} */
	const payload = {
		brand,
		mpn,
		quantity,
		displayedPricing,
		vendorTireCapture: parseVendorTireCapture(detail),
	};

	if (catalog !== '') {
		payload.catalog = catalog;
	}

	return { ok: true, payload };
}

/**
 * @param {{ brand: string, mpn: string, quantity: number, displayedPricing: number, catalog?: string, vendorTireCapture: Record<string, unknown>|null }} payload
 * @returns {HTMLButtonElement}
 */
function buildAddToCartElement(payload) {
	const button = document.createElement('button');
	button.className = 'add-to-cart enabled';
	button.dataset.sku = payload.mpn;
	button.dataset.skuStripped = payload.mpn;
	button.dataset.brand = payload.brand;
	button.dataset.quantity = String(payload.quantity);
	button.dataset.salePrice = String(payload.displayedPricing);
	button.dataset.cartSource = STEER_SOURCE;

	if (payload.catalog) {
		button.dataset.catalog = payload.catalog;
	}

	if (payload.vendorTireCapture !== null) {
		button.dataset.vendorTireCapture = JSON.stringify(payload.vendorTireCapture);
	}

	return button;
}

/**
 * @param {string} reason
 * @returns {string}
 */
function getSteerAddToCartFailureMessage(reason) {
	if (reason === ADD_TO_CART_FAILURE_REASON.MISSING_BRAND
		|| reason === ADD_TO_CART_FAILURE_REASON.MISSING_DISPLAYED_PRICING
		|| reason === ADD_TO_CART_FAILURE_REASON.MISSING_MPN
		|| reason === ADD_TO_CART_FAILURE_REASON.MISSHAPEN_PAYLOAD) {
		return 'Unable to add this item to your cart. Product details are incomplete.';
	}

	if (reason === ADD_TO_CART_FAILURE_REASON.CART_MODULE_LOAD_FAILED
		|| reason === ADD_TO_CART_FAILURE_REASON.CART_UNAVAILABLE) {
		return 'Unable to add this item to your cart right now. Please try again.';
	}

	const javascriptContent = globalThis.javascriptContent;
	if (javascriptContent && typeof javascriptContent.cart_item_add_error === 'string') {
		return javascriptContent.cart_item_add_error;
	}

	return 'Unable to add this item to your cart.';
}

/**
 * @param {string} message
 * @returns {void}
 */
function showSteerAddToCartFailureNotification(message) {
	const notify = globalThis.rp_app?.notify;
	if (typeof notify?.add_notification !== 'function') {
		return;
	}

	notify.add_notification({
		message,
		type: 'error',
	});
}

/**
 * @param {string} reason
 * @returns {void}
 */
function failSteerAddToCartEarly(reason) {
	showSteerAddToCartFailureNotification(getSteerAddToCartFailureMessage(reason));
}

/**
 * Clears body-level overlay spinners without relying on the overlaySpinner singleton ID.
 *
 * @returns {void}
 */
function removeSteerBodySpinners() {
	document.querySelectorAll('body > .spinner-wrap').forEach((spinnerElement) => {
		spinnerElement.remove();
	});
}

/**
 * @param {{ _cart_ele?: { hasClass: (className: string) => boolean }, _cart_ele_id?: string }} cart
 * @param {string} className
 * @returns {boolean}
 */
function cartElementHasClass(cart, className) {
	const cartElement = cart._cart_ele;
	if (cartElement && typeof cartElement.hasClass === 'function') {
		return cartElement.hasClass(className);
	}

	const rp$ = globalThis.rp$;
	if (typeof rp$ !== 'function') {
		return false;
	}

	const cartElementId = cart._cart_ele_id || 'rp_global_add_to_cart';
	const $cartElement = rp$('#' + cartElementId);
	if (typeof $cartElement.hasClass !== 'function') {
		return false;
	}

	return $cartElement.hasClass(className);
}

/**
 * @param {{ _cart_ele?: { hasClass: (className: string) => boolean }, _cart_ele_id?: string }} cart
 * @returns {boolean}
 */
function cartElementHasSaveFailed(cart) {
	return cartElementHasClass(cart, 'save_failed');
}

/**
 * @param {{ _cart_ele?: { hasClass: (className: string) => boolean }, _cart_ele_id?: string }} cart
 * @returns {boolean}
 */
function cartElementHasUnsuccessfulSave(cart) {
	return cartElementHasClass(cart, 'save_failed') || cartElementHasClass(cart, 'save_warning');
}

/**
 * Shows the add-to-cart modal that cart.js already built during a failed save.
 *
 * @param {{ _cart_ele_id?: string }} cart
 * @returns {void}
 */
function showSteerCartConfirmationModal(cart) {
	const rp$ = globalThis.rp$;
	if (typeof rp$ !== 'function') {
		return;
	}

	const cartElementId = cart._cart_ele_id || 'rp_global_add_to_cart';
	rp$('#' + cartElementId)
		.show()
		.find('.modal')
		.modal({ show: true });
}

/**
 * @param {{ _cart_ele?: { hasClass: (className: string) => boolean }, _cart_ele_id?: string, open_cart: () => void }} cart
 * @param {Record<string, unknown> | null} [ga4Payload]
 * @returns {void}
 */
function completeSteerAddToCart(cart, ga4Payload = null) {
	removeSteerBodySpinners();

	if (cartElementHasUnsuccessfulSave(cart)) {
		showSteerCartConfirmationModal(cart);
		return;
	}

	if (ga4Payload !== null) {
		trackSteerGa4Event(STEER_GA4_EVENT.ADD_TO_CART, ga4Payload);
	}

	cart.open_cart();
}

/**
 * Same spinner + callback pattern as the delegated `.add-to-cart.enabled` click handler in cart.js.
 *
 * @param {import('public/js/components/cart')} cart
 * @param {HTMLButtonElement} button
 * @param {Record<string, unknown> | null} [ga4Payload]
 * @returns {void}
 */
function addSteerProductToCart(cart, button, ga4Payload = null) {
	const rp$ = globalThis.rp$;
	if (typeof rp$ !== 'function') {
		return;
	}

	rp$('body').overlaySpinner();
	rp$('.spinner-overlay').css('position', 'fixed');
	rp$('.spinner-icon').css('position', 'fixed');

	cart.add_product_from_ele(button, {
		ignorePageQuantity: true,
		callback: function onSteerAddToCartComplete() {
			completeSteerAddToCart(cart, ga4Payload);
		},
	});
}

/**
 * @param {Record<string, unknown>} context
 */
function handleVehiclePickerInitialize(context) {
	trackSteerGa4Event(STEER_GA4_EVENT.VEHICLE_PICKER_INITIALIZE, {
		...context,
		source: STEER_SOURCE,
	});
	openVehiclePickerFresh();
}

/**
 * @param {Record<string, unknown>} detail
 * @returns {string}
 */
function readSteerPagePath(detail) {
	return readTrimmedString(detail.path)
		|| readTrimmedString(detail.page_path)
		|| readTrimmedString(detail.page)
		|| readTrimmedString(detail.steerPath);
}

/**
 * Prefers STEER's echoed visitor_id; falls back to the mount attribute we set.
 *
 * @param {Record<string, unknown>} detail
 * @returns {string}
 */
function readSteerVisitorId(detail) {
	const fromEvent = readTrimmedString(detail.visitor_id);
	if (fromEvent !== '') {
		return fromEvent;
	}

	const mountElement = globalThis.document?.getElementById('steer');
	const fromMount = mountElement instanceof HTMLElement
		? readTrimmedString(mountElement.dataset.visitorId)
		: '';

	return resolveSteerVisitorId(fromMount);
}

/**
 * @param {unknown} detail
 */
function handlePageChange(detail) {
	const context = normalizeSteerDetail(detail);
	const path = readSteerPagePath(context);
	if (path === '') {
		return;
	}

	persistSteerExperienceSession({
		pagePath: path,
		visitorId: readSteerVisitorId(context),
	});
}

/**
 * @param {unknown} detail
 */
function handleViewItem(detail) {
	const context = normalizeSteerDetail(detail);
	const brand = readTrimmedString(context.brand);
	const mpn = readTrimmedString(context.mpn);
	if (!rememberViewedItem(brand, mpn)) {
		return;
	}

	trackSteerGa4Event(
		STEER_GA4_EVENT.VIEW_ITEM,
		buildSteerViewItemPayload({
			brand,
			mpn,
			name: readTrimmedString(context.name),
			price: readOptionalPrice(context.price),
			visitorId: readSteerVisitorId(context),
		}),
	);
}

/**
 * @param {unknown} detail
 */
function handleAddToCart(detail) {
	const parseResult = parseAddToCartPayload(detail);
	if (!parseResult.ok) {
		failSteerAddToCartEarly(parseResult.reason);
		return;
	}

	const context = normalizeSteerDetail(detail);
	const ga4Payload = buildSteerAddToCartPayload({
		brand: parseResult.payload.brand,
		mpn: parseResult.payload.mpn,
		quantity: parseResult.payload.quantity,
		name: readTrimmedString(context.name),
		price: parseResult.payload.displayedPricing,
		visitorId: readSteerVisitorId(context),
	});

	getCart().then(() => {
		const cart = globalThis.rp_app?.cart;
		if (typeof cart?.add_product_from_ele !== 'function') {
			failSteerAddToCartEarly(ADD_TO_CART_FAILURE_REASON.CART_UNAVAILABLE);
			return;
		}

		const button = buildAddToCartElement(parseResult.payload);
		addSteerProductToCart(cart, button, ga4Payload);
	}).catch(() => {
		failSteerAddToCartEarly(ADD_TO_CART_FAILURE_REASON.CART_MODULE_LOAD_FAILED);
	});
}

/** @type {Record<string, (context: Record<string, unknown>) => void>} */
const STEER_INBOUND_HANDLERS = {
	'add-to-cart': handleAddToCart,
	'page-change': handlePageChange,
	'steer-embed-navigate': handlePageChange,
	'vehicle-picker-initialize': handleVehiclePickerInitialize,
	'view-item': handleViewItem,
};

function registerSteerInboundListeners() {
	Object.entries(STEER_INBOUND_HANDLERS).forEach(([eventName, handler]) => {
		globalThis.addEventListener(eventName, (event) => {
			if (eventName === 'add-to-cart') {
				handler(event.detail);
				return;
			}

			handler(normalizeSteerDetail(event.detail));
		});
	});
}

module.exports = {
	ADD_TO_CART_FAILURE_REASON,
	STEER_GA4_EVENT,
	STEER_INBOUND_HANDLERS,
	STEER_SOURCE,
	STEER_VEHICLE_SELECTED_DOMAIN_KEY,
	addSteerProductToCart,
	buildAddToCartElement,
	cartElementHasSaveFailed,
	cartElementHasUnsuccessfulSave,
	completeSteerAddToCart,
	getSteerAddToCartFailureMessage,
	handleAddToCart,
	handlePageChange,
	handleVehiclePickerInitialize,
	handleViewItem,
	normalizeSteerDetail,
	openVehiclePickerFresh,
	parseAddToCartPayload,
	parseBooleanWireValue,
	parseQuery,
	parseVendorTireCapture,
	parseVehicleSelected,
	readSteerVisitorId,
	registerSteerInboundListeners,
	removeSteerBodySpinners,
	showSteerCartConfirmationModal,
};
